import { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  AUTO_ASSIGN_STAGING_REASONS,
  PRIORITY_SCHEDULER_TAB_LABELS,
} from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { IAttemptListItemResponseForWorkOrder } from "@miplatform/types";
import { isSameDay } from "@miplatform/utils";
import { Box } from "@mui/material";
import { useCalendarContext, useCalendarLockContext } from "../../../providers";
import AssignableUserColumn from "../UnassignedWorkOrders/AssignableUserColumn";
import { AssignableUserContainer } from "../UnassignedWorkOrders/AssignableUserContainer";
import { UnassignedWorkOrders } from "../UnassignedWorkOrders/UnassignedWorkOrders";
import { useAutoAssignStatusIndicator } from "../useAutoAssignStatusIndicator";
import { AssignmentViewActions } from "./AssignmentViewActions";
import { CalendarLockBanner } from "./CalendarLockBanner";
import { CancelAssignmentStagingModal } from "./CancelAssignmentStagingModal";
import {
  ASSIGNMENT_MODES,
  AssignmentMode,
  useAssignmentStaging,
} from "./useAssignmentStaging";
import { useNHCPriorityScheduler } from "./useNHCPriorityScheduler";

const COLUMN_WIDTH = 180;
const HOUR_HEIGHT = 60;

interface AssignmentViewProps {
  scheduler: ReturnType<typeof useNHCPriorityScheduler>;
  assignmentMode?: AssignmentMode;
  workOrders: IAttemptListItemResponseForWorkOrder[];
}

export function AssignmentView({
  scheduler,
  assignmentMode = ASSIGNMENT_MODES.UNASSIGNED,
  workOrders,
}: Readonly<AssignmentViewProps>) {
  const { classes, cx } = useStyles();
  const {
    zoom,
    date,
    users,
    calendarSet,
    events,
    refreshData,
    handleSelectUser,
    autoAssignStatusUpdates,
  } = useCalendarContext();
  const {
    isLockedByOtherUser,
    acquireCalendarLock,
    createCalendarLockRenewal,
    clearLockHolderState,
  } = useCalendarLockContext();
  const [discardStagingModalOpen, setDiscardStagingModalOpen] = useState(false);
  const isNotReadyMode = assignmentMode === ASSIGNMENT_MODES.NOT_READY;
  const lockRenewalCleanupRef = useRef<(() => void) | null>(null);

  const {
    activeAssignableUserIds,
    assignableUsersAvailableTime,
    boardExpandedUserId,
    effectiveSelectedUserId,
    expandedUsers,
    getAssignableUsersAvailableTime,
    handleAddAssignableUser,
    handleBoardExpandedChange,
    handleCloseUserColumn,
    handleUserSelect,
    isLoading,
    isWorkOrdersLoading,
    reloadUnassignedWorkOrders,
  } = scheduler;

  const {
    stagedByUserId,
    stagedIds,
    confirmedAttemptIds,
    totalStagedCount,
    isConfirming,
    stageWorkOrderForUser,
    stageWorkOrdersForUser,
    undoLastTransfer,
    confirmStaging,
    clearStaging,
    unstageAttempt,
    getStagedForUser,
    getRemainingTemplateDurationForUser,
    autoAssignWorkOrders,
    isAutoAssignLoading,
  } = useAssignmentStaging(calendarSet?.config ?? null, date, events);
  const { isAutoAssignActive, loadAutoAssignStatuses } =
    useAutoAssignStatusIndicator({
      calendarSetId: calendarSet?.config?.id,
      eventType: calendarSet?.config?.eventType,
      autoAssignStatusUpdates,
    });

  const totalStagedCountRef = useRef(totalStagedCount);
  totalStagedCountRef.current = totalStagedCount;
  const prevStagedCountRef = useRef(totalStagedCount);

  const stopLockRenewal = useCallback(() => {
    if (lockRenewalCleanupRef.current) {
      lockRenewalCleanupRef.current();
      lockRenewalCleanupRef.current = null;
    }
  }, []);

  const clearStagingRef = useRef(clearStaging);
  clearStagingRef.current = clearStaging;

  const releaseLockSession = useCallback(() => {
    stopLockRenewal();
    clearLockHolderState();
  }, [clearLockHolderState, stopLockRenewal]);

  const handleLockFailure = useCallback(() => {
    clearStagingRef.current();
    releaseLockSession();
  }, [releaseLockSession]);

  const startLockRenewal = useCallback(
    (initialLockedUntil?: string | null) => {
      if (lockRenewalCleanupRef.current) {
        return;
      }
      lockRenewalCleanupRef.current = createCalendarLockRenewal(
        handleLockFailure,
        initialLockedUntil
      );
    },
    [createCalendarLockRenewal, handleLockFailure]
  );

  const ensureCalendarLock = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (isLockedByOtherUser) {
        return false;
      }
      if (!force && lockRenewalCleanupRef.current) {
        return true;
      }

      const lockResponse = await acquireCalendarLock(handleLockFailure);

      if (!lockResponse?.lockAcquired) {
        return false;
      }

      startLockRenewal(lockResponse.lockedUntil);
      return true;
    },
    [
      acquireCalendarLock,
      handleLockFailure,
      isLockedByOtherUser,
      startLockRenewal,
    ]
  );

  useEffect(() => {
    getAssignableUsersAvailableTime();
  }, [date]);

  useEffect(() => {
    const previousCount = prevStagedCountRef.current;
    prevStagedCountRef.current = totalStagedCount;

    if (totalStagedCount > 0) {
      startLockRenewal();
      return;
    }

    if (previousCount > 0) {
      releaseLockSession();
    }
  }, [releaseLockSession, startLockRenewal, totalStagedCount]);

  useEffect(() => {
    if (!isLockedByOtherUser || totalStagedCountRef.current === 0) {
      return;
    }
    clearStagingRef.current();
    releaseLockSession();
  }, [isLockedByOtherUser, releaseLockSession]);

  useEffect(() => {
    return () => {
      stopLockRenewal();
    };
  }, [stopLockRenewal]);

  const prevAssignmentModeRef = useRef(assignmentMode);
  useEffect(() => {
    if (prevAssignmentModeRef.current === assignmentMode) {
      return;
    }
    prevAssignmentModeRef.current = assignmentMode;
    clearStaging();
  }, [assignmentMode, clearStaging]);

  const handleWorkOrderClick = async (
    attempt: IAttemptListItemResponseForWorkOrder
  ) => {
    if (isNotReadyMode && totalStagedCount > 0) {
      return;
    }
    const targetUserId = effectiveSelectedUserId ?? activeAssignableUserIds[0];
    if (!targetUserId) {
      return;
    }
    if (!effectiveSelectedUserId && activeAssignableUserIds[0]) {
      handleUserSelect(activeAssignableUserIds[0]);
    }

    const lockAcquired = await ensureCalendarLock();
    if (!lockAcquired) {
      return;
    }

    const result = stageWorkOrderForUser(attempt, targetUserId);
    if (!result.staged && totalStagedCountRef.current === 0) {
      releaseLockSession();
    }
    if (
      !result.staged &&
      result.reason === AUTO_ASSIGN_STAGING_REASONS.INSUFFICIENT_WINDOW_CAPACITY
    ) {
      toastService.error({
        text: "The selected template time window is already filled.",
      });
    }
  };

  const handleBulkAssign = async (
    attempts: IAttemptListItemResponseForWorkOrder[]
  ) => {
    const targetUserId = effectiveSelectedUserId ?? activeAssignableUserIds[0];
    if (!targetUserId || attempts.length === 0) {
      return;
    }
    if (!effectiveSelectedUserId && activeAssignableUserIds[0]) {
      handleUserSelect(activeAssignableUserIds[0]);
    }

    const lockAcquired = await ensureCalendarLock();
    if (!lockAcquired) {
      return;
    }

    const result = stageWorkOrdersForUser(attempts, targetUserId);
    if (result.addedCount === 0 && totalStagedCountRef.current === 0) {
      releaseLockSession();
    }
    if (result.blockedCount > 0) {
      toastService.error({
        text: `${result.blockedCount} work order(s) could not be staged because template time windows are already filled.`,
      });
    }
  };

  const handleAutoAssign = async () => {
    const lockAcquired = await ensureCalendarLock();
    if (!lockAcquired) {
      return;
    }

    try {
      await autoAssignWorkOrders();
      await loadAutoAssignStatuses();
    } finally {
      if (totalStagedCountRef.current === 0) {
        releaseLockSession();
      }
    }
  };

  const handleRequestDiscardStaging = () => {
    if (totalStagedCount > 0) {
      setDiscardStagingModalOpen(true);
    }
  };

  const handleCloseDiscardStagingModal = () => {
    setDiscardStagingModalOpen(false);
  };

  const handleConfirmDiscardStaging = () => {
    clearStaging();
    setDiscardStagingModalOpen(false);
  };

  const handleConfirmStaging = async () => {
    const lockAcquired = await ensureCalendarLock({ force: true });
    if (!lockAcquired) {
      return;
    }

    const success = await confirmStaging();
    if (success) {
      refreshData();
      reloadUnassignedWorkOrders();
      getAssignableUsersAvailableTime();
      toastService.success({
        text: "Work orders assigned successfully.",
      });
    }
  };

  const getRemainingDurationForUser = useCallback(
    (userId?: string) => {
      if (!userId) {
        return 0;
      }
      const user = assignableUsersAvailableTime.find(
        (user) => user.id === userId
      );

      if (!user) {
        return 0;
      }

      return (
        user.availableTime * 60 -
        getStagedForUser(userId).reduce((acc, curr) => {
          return (
            acc +
            (curr.attempt.assigneeExpectedDuration ??
              curr.attempt.expectedDuration ??
              0)
          );
        }, 0)
      );
    },
    [assignableUsersAvailableTime, getStagedForUser]
  );

  const hasVisibleAssignableColumns = activeAssignableUserIds.length > 0;
  const selectedUserId = effectiveSelectedUserId ?? activeAssignableUserIds[0];

  return (
    <Box className={classes.container}>
      <Box
        className={cx(
          classes.content,
          isLockedByOtherUser && classes.contentLocked
        )}
        inert={isLockedByOtherUser || undefined}
      >
        <Box className={classes.assignmentPanel}>
          <AssignableUserContainer
            assignableUsersAvailableTime={assignableUsersAvailableTime}
            isLoading={isLoading}
            onUserSelect={handleUserSelect}
            selectedUserId={effectiveSelectedUserId}
            activeAssignableUserIds={activeAssignableUserIds}
            stagedByUserId={stagedByUserId}
            isAutoAssignLoading={isAutoAssignLoading}
            isAutoAssignActive={isAutoAssignActive}
            onAddAssignableUser={handleAddAssignableUser}
            showAutoAssign={!isNotReadyMode}
            onAutoAssign={isNotReadyMode ? undefined : handleAutoAssign}
          />
          <UnassignedWorkOrders
            rawUnassignedWorkOrders={workOrders}
            isWorkOrdersLoading={isWorkOrdersLoading}
            stagedAttemptIds={stagedIds}
            confirmedAttemptIds={confirmedAttemptIds}
            hasAssignableUserColumn={hasVisibleAssignableColumns}
            remainingDurationForUser={getRemainingDurationForUser(
              selectedUserId
            )}
            remainingDurationByTemplateId={
              selectedUserId
                ? getRemainingTemplateDurationForUser(selectedUserId)
                : undefined
            }
            onWorkOrderClick={handleWorkOrderClick}
            onBulkAssign={isNotReadyMode ? undefined : handleBulkAssign}
            title={
              isNotReadyMode
                ? PRIORITY_SCHEDULER_TAB_LABELS.NOT_READY
                : PRIORITY_SCHEDULER_TAB_LABELS.UNASSIGNED
            }
            bulkAssignEnabled={!isNotReadyMode}
            ghostOtherWorkOrders={isNotReadyMode && totalStagedCount > 0}
            countChipTone={
              isNotReadyMode
                ? ASSIGNMENT_MODES.NOT_READY
                : ASSIGNMENT_MODES.UNASSIGNED
            }
            isNotReadyMode={isNotReadyMode}
            selectedUserId={effectiveSelectedUserId}
          />
          {hasVisibleAssignableColumns && (
            <AssignmentViewActions
              stagedCount={totalStagedCount}
              disabled={
                totalStagedCount === 0 || isConfirming || isLockedByOtherUser
              }
              onCancel={handleRequestDiscardStaging}
              onUndo={undoLastTransfer}
              onConfirm={handleConfirmStaging}
            />
          )}
          <CancelAssignmentStagingModal
            open={discardStagingModalOpen}
            stagedCount={totalStagedCount}
            onClose={handleCloseDiscardStagingModal}
            onConfirmCancel={handleConfirmDiscardStaging}
          />
        </Box>

        {expandedUsers.map((user) => {
          const isBoardExpandedUser =
            boardExpandedUserId !== null && user.id === boardExpandedUserId;
          const currentUser = users.find((u) => u.id === user.id);
          if (boardExpandedUserId !== null && user.id !== boardExpandedUserId) {
            return null;
          }

          return (
            <AssignableUserColumn
              key={user.id}
              user={currentUser || user}
              selectedDate={date}
              selected={user.id === effectiveSelectedUserId}
              columnWidth={COLUMN_WIDTH}
              hourHeight={HOUR_HEIGHT}
              zoom={zoom}
              stagedAssignments={getStagedForUser(user.id)}
              assignedEvents={events.filter(
                (event) =>
                  event.assignedUserId === user.id &&
                  (isBoardExpandedUser ||
                    isSameDay(event.scheduledStartDate, date))
              )}
              onRemoveStagedAttempt={(attemptId) =>
                unstageAttempt(user.id, attemptId)
              }
              onClose={() => handleCloseUserColumn(user.id)}
              boardExpanded={isBoardExpandedUser}
              onBoardExpandedChange={(expanded: boolean) => {
                handleBoardExpandedChange(user.id, expanded);
                handleSelectUser(expanded ? user : null);
              }}
            />
          );
        })}
      </Box>

      {isLockedByOtherUser && (
        <Box className={classes.lockOverlay}>
          <CalendarLockBanner />
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    position: "relative",
    minHeight: 0,
  },
  content: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: theme.spacing(1),
    minHeight: 0,
  },
  contentLocked: {
    filter: "blur(4px)",
    pointerEvents: "none",
    userSelect: "none",
  },
  assignmentPanel: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    overflow: "auto",
    flex: 1,
  },
  lockOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "stretch",
    pointerEvents: "none",
  },
}));
